/* eslint-disable @typescript-eslint/no-explicit-any */
import { numberFormat } from 'highcharts';
import { colors } from '../../styles/colors/colors.config';
import { typographyConfig } from '../../styles/typography/typography.config';

// Types
import type {
  DataLabelsFormatterCallbackFunction,
  Options,
  Point,
  Series
} from 'highcharts';

// For storybook display only
const generateTooltipHtml = (date: string): string =>
  `<div class="text-gray-000 b1">${date}</div>`;

// For storybook display only
const dataLabelFormatter: DataLabelsFormatterCallbackFunction = function () {
  const point: Point = this.point;
  const series: Series = this.series;
  const chartSeries: Series[] = series.chart.series;
  const value: number = parseFloat(numberFormat(point.y || 0, 2));

  if (series.name !== chartSeries[1]?.name) {
    return value.toString();
  }

  const prevValue: number = parseFloat(
    numberFormat(chartSeries[0]?.data[point.index]?.y || 0, 2)
  );
  const percentageChange: number = ((value - prevValue) / prevValue) * 100;
  const arrowDirection: string = percentageChange >= 0 ? 'up' : 'down';
  const absPercentageChange: number = Math.abs(
    parseFloat(percentageChange.toFixed(2))
  );

  const arrow = `<span class="material-symbols-outlined icon-small ${arrowDirection}">arrow_${arrowDirection}ward</span>`;
  return `<div class="flex justify-center">${value} <span class="${arrowDirection}">${absPercentageChange}% ${arrow}</span></div>`;
};

export const defaultConfig: Options = {
  series: [
    {
      name: 'Previous Period',
      data: [
        {
          y: 150,
          tooltipHtml: generateTooltipHtml('Oct 04, 2023 - Nov 05, 2023')
        }
      ],
      color: colors['gray-300'],
      type: 'column' as any,
      states: {
        hover: {
          enabled: false
        },
        inactive: {
          opacity: 1
        }
      }
    },
    {
      name: 'Current Period',
      data: [
        {
          y: 189,
          tooltipHtml: generateTooltipHtml('Nov 06, 2023 - Dec 07, 2023')
        }
      ],
      color: colors['gray-700'],
      type: 'column' as any,
      states: {
        hover: {
          enabled: false
        },
        inactive: {
          opacity: 1
        }
      }
    }
  ],
  yAxis: {
    visible: false
  },
  xAxis: {
    visible: false
  },
  legend: {
    symbolHeight: 0.001,
    symbolWidth: 0.001,
    symbolRadius: 0.001,
    align: 'center',
    verticalAlign: 'bottom',
    layout: 'horizontal',
    x: 0,
    margin: 0,
    itemStyle: {
      fontSize: typographyConfig.styles.b2.size,
      fontFamily: typographyConfig.styles.b2.fontFamily,
      fontWeight: typographyConfig.styles.b2.weight
    },
    itemDistance: 21.6
  },
  plotOptions: {
    column: {
      pointWidth: 79,
      pointPadding: 0,
      groupPadding: 0,
      borderWidth: 0,
      maxPointWidth: 138,
      dataLabels: {
        enabled: true,
        align: 'center',
        verticalAlign: 'top',
        y: -25,
        x: 0,
        formatter: dataLabelFormatter,
        style: {
          color: colors['gray-800'],
          fontSize: typographyConfig.styles.c2.size,
          fontFamily: typographyConfig.styles.c2.fontFamily,
          fontWeight: typographyConfig.styles.c2.weight
        }
      },
      states: {
        hover: {
          enabled: false
        }
      }
    }
  },

  tooltip: {
    useHTML: true,
    formatter() {
      const point: any = this.point;
      return point && point.tooltipHtml
        ? `<div class="text-center">${point.tooltipHtml}</div>`
        : '';
    },
    positioner(labelWidth, labelHeight, point) {
      const tooltipX = point.plotX + this.chart.plotLeft - labelWidth / 2;
      const tooltipY = point.plotY + this.chart.plotTop - labelHeight - 32; // Move up by 32px

      return { x: tooltipX, y: tooltipY };
    }
  }
};
